import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  badge?: boolean;
}

// Navigation organisée par mission du fondateur (et non par objet) :
// Piloter → Amorcer → Animer & modérer → Configurer → Maintenir.

// PILOTER : « est-ce que la sauce prend ? »
const piloterItems: NavItem[] = [
  { path: '/', label: 'Tour de contrôle', icon: '📊' },
  { path: '/activity', label: 'Activité récente', icon: '🔔' },
  { path: '/command-board/analytics', label: 'Analytics', icon: '📈' },
  { path: '/command-board/engagement', label: 'Engagement', icon: '⚡' },
];

// AMORCER : amorçage de la marketplace (growth / seeds)
const amorcerItems: NavItem[] = [
  { path: '/command-board', label: 'Pilotage seeds', icon: '🎯' },
  { path: '/command-board/accounts', label: 'Comptes Seeds', icon: '🌱' },
  { path: '/command-board/offers', label: 'Offres Seeds', icon: '📝' },
];

// ANIMER & MODÉRER : le quotidien (communauté, contenu, modération, revenus)
const animerItems: NavItem[] = [
  { path: '/videos', label: 'Vidéos', icon: '🎬', badge: true },
  { path: '/reports', label: 'Signalements', icon: '🚩' },
  { path: '/candidates', label: 'Candidats', icon: '👥' },
  { path: '/employers', label: 'Recruteurs', icon: '🏢' },
  { path: '/offers', label: 'Offres', icon: '📋' },
  { path: '/subscriptions', label: 'Abonnements', icon: '💳' },
];

// CONFIGURER : les leviers du produit
const configurerItems: NavItem[] = [
  { path: '/referentials', label: 'Référentiels', icon: '⚙️' },
  { path: '/command-board/skill-families', label: 'Familles transversales', icon: '🌐' },
  { path: '/music', label: 'Musiques', icon: '🎵' },
  { path: '/admins', label: 'Comptes admin', icon: '🔐' },
];

// MAINTENIR : data-ops & technique (replié par défaut, hors quotidien)
const maintenirItems: NavItem[] = [
  { path: '/command-board/skill-usage', label: 'Usage compétences', icon: '📉' },
  { path: '/command-board/skill-proximity', label: 'Redondances', icon: '🔗' },
  { path: '/logs', label: 'Logs', icon: '📄' },
];

function NavSection({ title, items, location, navigate, accentClass, collapsible, defaultCollapsed }: {
  title: string;
  items: NavItem[];
  location: { pathname: string };
  navigate: (path: string) => void;
  accentClass?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed ?? false);

  return (
    <>
      {collapsible ? (
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between text-[10px] uppercase tracking-widest text-white/30 hover:text-white/50 font-semibold px-3 mb-2 mt-4 first:mt-0 transition-colors"
        >
          <span>{title}</span>
          <span className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}>›</span>
        </button>
      ) : (
        <p className="text-[10px] uppercase tracking-widest text-white/30 font-semibold px-3 mb-2 mt-4 first:mt-0">
          {title}
        </p>
      )}
      {!collapsed && (
        <ul className="space-y-0.5">
          {items.map((item) => {
            const isActive = item.path === '/'
              ? location.pathname === '/'
              : item.path === '/command-board'
              ? location.pathname === '/command-board'
              : location.pathname.startsWith(item.path);
            return (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? `${accentClass || 'bg-[#00244F]'} text-white shadow-sm`
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="text-base w-6 text-center">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <aside className="w-[250px] min-h-screen bg-[#001B3D] flex flex-col text-white flex-shrink-0">
      <div className="px-5 py-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#365A8C] flex items-center justify-center text-lg font-bold">
            PJ
          </div>
          <div>
            <h1 className="text-base font-semibold leading-tight">Power Jobs</h1>
            <p className="text-xs text-white/50">Administration</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <NavSection title="Piloter" items={piloterItems} location={location} navigate={navigate} />

        <div className="my-4 mx-3 border-t border-white/10" />

        <NavSection title="Amorcer" items={amorcerItems} location={location} navigate={navigate} accentClass="bg-indigo-600/40" />

        <div className="my-4 mx-3 border-t border-white/10" />

        <NavSection title="Animer & modérer" items={animerItems} location={location} navigate={navigate} accentClass="bg-emerald-600/40" />

        <div className="my-4 mx-3 border-t border-white/10" />

        <NavSection title="Configurer" items={configurerItems} location={location} navigate={navigate} accentClass="bg-amber-600/40" />

        <div className="my-4 mx-3 border-t border-white/10" />

        <NavSection title="Maintenir" items={maintenirItems} location={location} navigate={navigate} accentClass="bg-slate-600/40" collapsible defaultCollapsed />
      </nav>

      <div className="px-5 py-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#365A8C] flex items-center justify-center text-xs font-bold">
            {user?.email?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.email || 'Admin'}</p>
            <p className="text-[10px] text-white/40">Administrateur</p>
          </div>
        </div>
        <p className="text-[10px] text-white/25 mt-3">Version 1.0</p>
      </div>
    </aside>
  );
};

export default Sidebar;
